import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, setAuthToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/errors";
import PasswordStrength, { passwordOk } from "@/components/PasswordStrength";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setAuthToken(data.token);
      await refresh();
      toast.success("Password updated — you're signed in");
      nav("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1 max-w-md mx-auto w-full px-4 py-16">
        <Card className="tactile-card p-8 rounded-md" data-testid="reset-password-card">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#00E676]">ACCOUNT RECOVERY</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-2">Choose a new password</h1>
          {!token ? (
            <div className="mt-6 text-sm text-[#A0A7B0]" data-testid="reset-password-missing">
              This link is missing its token. <Link to="/forgot-password" className="text-[#00E676] hover:underline">Request a new one</Link>.
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="pw" className="text-xs text-[#A0A7B0]">New password</Label>
                <Input id="pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[#0C0D0E] border-[#282C30] mt-1" data-testid="reset-password-input" />
                <PasswordStrength password={password} />
              </div>
              <div>
                <Label htmlFor="pw2" className="text-xs text-[#A0A7B0]">Confirm password</Label>
                <Input id="pw2" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="bg-[#0C0D0E] border-[#282C30] mt-1" data-testid="reset-password-confirm" />
              </div>
              {error && <div className="text-sm text-[#FF5C5C]" data-testid="reset-password-error">{error}</div>}
              <Button type="submit" className="btn-emerald w-full h-11" disabled={loading || !passwordOk(password)} data-testid="reset-password-submit">
                {loading ? "Saving…" : "Set new password"}
              </Button>
              <p className="text-xs text-[#606872]">For your safety, every other device will be signed out.</p>
            </form>
          )}
        </Card>
      </div>
      <Footer />
    </div>
  );
}
